import React, { useContext } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Badge, DotList } from '@motorway/mw-highway-code';
import { VRM } from '@motorway/mw-highway-code/alpha';
import { SIZE } from '@motorway/mw-highway-code/enums';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { formatPrice } from 'Utilities/formatPrice';
import { useDisplayPrice } from 'Utilities/useDisplayPrice';

import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';
import { Tooltip } from 'Storybook/components/local/Tooltip/Tooltip';
import { TooltipAnchor } from 'Storybook/components/local/Tooltip/TooltipAnchor';

import { useVehicleCard } from '../../../account/Components/VehicleCard/useVehicleCard';
import useTimeToEnquiryExpire from '../../../valuations/Hooks/useTimeToEnquiryExpire';

import { getExpiredBadgeData } from './VehicleCardReturningUser.helpers';
import Texts from './vehicleCardReturningUser.text.json';
import { VehicleCardReturningUserCTA } from './VehicleCardReturningUserCTA';

import styles from './VehicleCardReturningUser.module.scss';

const LocalT = new MDText(Texts);

const ExpiredBadge: React.FC<{ days: number }> = ({ days }) => {
  const { icon, text, variant } = getExpiredBadgeData(days);

  return (
    <div>
      <Badge reverse icon={() => icon} {...{ text, variant }} />
    </div>
  );
};

export const ReturningValuation: React.FC<{
  displayPrice?: string | null;
  loading: boolean;
  price?: number | null;
}> = ({ displayPrice, loading, price }) => (
  <>
    <span>{displayPrice ?? formatPrice(price ?? '')}</span>
    <div className={cx(styles.loadingValuation, { [styles.finishedLoading]: !loading })} data-testid="loadingValuation">
      <LoadingArrow />
    </div>
  </>
);

export const VehicleCardReturningUser = () => {
  const { vehicleLookupState: latestVehicle } = useContext(VehicleLookupContext);
  const {
    ctaProps,
    estimateData,
    hasReservePrice,
    isEnquiryExpirable,
    isEnquiryExpired,
    isSellingButtonLoading,
    renderPrice,
  } = useVehicleCard({ vehicle: latestVehicle });

  const mileage = LocalT.translate('mileage', { mileage: latestVehicle.enquiry?.mileage?.toLocaleString() });
  const year = latestVehicle.manufactureYear ?? latestVehicle.year;
  const yearFormatted = year ? `${year}` : undefined;
  const state = estimateData?.enquiryStateSlug || latestVehicle.enquiry?.dpStateSlug;

  const enquiryDate = latestVehicle.enquiry?.createdAt ?? '';
  const { days } = useTimeToEnquiryExpire({ enquiryDate, vehicle: latestVehicle });

  // Quoted vehicles have no dpStateSlug, but should show the badge
  const isExpiryBadgeVisible = (!state || isEnquiryExpirable);

  const displayPrice = useDisplayPrice(renderPrice(), latestVehicle.enquiry?.dpEnquiryId, hasReservePrice);

  return (
    <div className={styles.component}>
      <div>
        <div className={styles.badge}>
          <VRM data-thc-vrm size={SIZE.SMALL} text={latestVehicle.vrm_pretty as string} />
        </div>
        {latestVehicle.enquiry?.valuation?.estimate ? (
          <div className={styles.priceBlock}>
            <div className={cx(styles.price, {
              [styles.expired]: isEnquiryExpired,
              [styles.priceRange]: Boolean(displayPrice),
            })}>
              <ReturningValuation
                { ...{
                  displayPrice,
                  loading: isSellingButtonLoading,
                  price: renderPrice(),
                }}
              />
            </div>
            {isEnquiryExpirable && (
              <Tooltip
                overlay={<div>{LocalT.translate('tooltip')}</div>}
              >
                <span>
                  <TooltipAnchor className={styles.tooltipAnchor} filledIcon={false} />
                </span>
              </Tooltip>
            )}
          </div>
        ) : null}
        <h5 className={styles.model}>{latestVehicle.make?.display_name} {latestVehicle.model}</h5>
        <DotList
          data-thc-dotlist
          narrow
          data={[
            { content: yearFormatted, key: 'year' },
            { content: latestVehicle.colour, key: 'fuel' },
            { content: mileage, key: 'mileage' },
          ]}
        />
        {isExpiryBadgeVisible && <ExpiredBadge days={days} />}
      </div>

      <VehicleCardReturningUserCTA
        {...ctaProps}
        vrm={latestVehicle.vrm as string}
      />
    </div>
  );
};
